import React from 'react';
import { fmt, fmtCount, fmtPct } from '../../utils/formatters';
import {
  GLYPH_TITLE, OutlookDay, callChip, dayGlyph, dayLabel, dollarScale, lowF, moveAmount, nextMoveDay, nextMoveLine,
  tomorrowDay, tomorrowFigure, tomorrowSentence,
} from '../../utils/weather';
import DeviationBar from './DeviationBar';
import WeatherGlyph from './WeatherGlyph';
import { Chip, NOTE } from './weatherUi';

/** Under a usual tomorrow, the thing to notice: the next day the weather moves, on the strip's scale. */
function NextMove({ days, d }: { days: OutlookDay[]; d: OutlookDay }) {
  const possible = d.label === 'possible';
  const kind = dayGlyph(d);
  return (
    <div data-next className="mt-4 grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 gap-y-2 rounded-[10px] border border-[#E6E2DE] bg-[#FAF9F7] px-4 py-3 sm:max-w-xl sm:grid-cols-[auto_minmax(0,1fr)_9rem]">
      <span className="text-[#4A2D5E]">{kind && <WeatherGlyph kind={kind} size={28} title={GLYPH_TITLE[kind]} />}</span>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-sans text-sm font-medium text-[#2D2A2E]">Next: {d.weekday}</span>
          <Chip tone={d.call === 'slower' ? 'slower' : 'busier'} kind="call">{callChip(d)}</Chip>
          {possible && <Chip tone="quiet" kind="possible">Possible</Chip>}
        </div>
        <p className="mt-0.5 font-sans text-sm tabular-nums text-[#2D2A2E]">
          <span className="whitespace-nowrap font-medium">{moveAmount(d)}</span>
          <span className="text-[#6B6670]"> against a usual {d.weekday}</span>
        </p>
      </div>
      <div className="col-span-2 sm:col-span-1">
        <DeviationBar point={d.dollars ?? 0} low={d.dollars_low ?? 0} high={d.dollars_high ?? 0} scale={dollarScale(days)} muted={possible} />
      </div>
    </div>
  );
}

// The headline card: the next open day, its dollars against a usual day (or a
// word when there is no number), one plain sentence, and the weather.

const FIGURE_TONE: Record<string, string> = { slower: 'text-[#B54A4A]', busier: 'text-[#2E6B4F]' };

export default function TomorrowCard({ days, down = false }: { days: OutlookDay[]; down?: boolean }) {
  const next = tomorrowDay(days);
  if (!next) return null;
  const { day: d, skipped } = next;
  const when = skipped ? `on ${d.weekday}` : 'tomorrow';
  const moved = d.call === 'slower' || d.call === 'busier';
  const kind = dayGlyph(d);
  const low = lowF(d);
  const figure = tomorrowFigure(d);
  return (
    <section aria-labelledby="weather-tomorrow" className="rounded-[14px] border border-[#E6E2DE] bg-white p-6 shadow-card">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 id="weather-tomorrow" className="font-serif text-lg text-[#2D2A2E]">{skipped ? d.weekday : 'Tomorrow'}</h2>
          <p className={NOTE}>{dayLabel(d.date)}</p>
          {skipped && <p className={`${NOTE} mt-0.5`}>Closed tomorrow. This is your next open day.</p>}
        </div>
        <div className="flex shrink-0 items-center gap-2.5 text-[#4A2D5E]">
          {kind && <WeatherGlyph kind={kind} size={36} title={GLYPH_TITLE[kind]} />}
          {d.weather && (
            <div className="text-right">
              <div className="font-sans text-sm font-medium tabular-nums text-[#2D2A2E]">
                {fmtCount(d.weather.high_f)}°
                {low != null && <span className="text-[#6B6670]"> / {fmtCount(low)}°</span>}
              </div>
              <div className={`${NOTE} whitespace-nowrap tabular-nums`}>rush rain {fmtPct(d.weather.rain_am_chance * 100)}</div>
            </div>
          )}
        </div>
      </div>
      <div data-figure className={`mt-4 font-sans text-[1.75rem] font-bold leading-[1.15] tabular-nums tracking-[-0.01em] md:text-[2rem] ${FIGURE_TONE[d.call] ?? 'text-[#2D2A2E]'}`}>
        {figure.split(' to ').map((part, i) => (
          <React.Fragment key={part}>{i > 0 && ' to '}<span className="whitespace-nowrap">{part}</span></React.Fragment>
        ))}
      </div>
      {moved && d.usual != null && (
        <p className={`${NOTE} mt-1 tabular-nums`}>against a usual {d.weekday} of {fmt(d.usual)}</p>
      )}
      <p className="mt-3 max-w-[60ch] font-sans text-sm leading-[1.6] text-[#2D2A2E]">
        {down ? 'No forecast this morning, so no call yet. It fills in when the forecast comes back.' : tomorrowSentence(d, when)}
      </p>
      {!down && d.call === 'normal' && (nextMoveDay(days, d) ? <NextMove days={days} d={nextMoveDay(days, d) as OutlookDay} /> : (
        <p data-next className={`${NOTE} mt-3 border-t border-[#E6E2DE] pt-3`}>{nextMoveLine(days, d)}</p>
      ))}
    </section>
  );
}
